import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import GeneralButtonsChoiceSegmentButton from 'lumen/components/general/buttons/choice-segment-button';
import SettingsRecommendationsSnowflakesRecommendationsSettingCard from 'lumen/components/settings/recommendations/snowflakes/recommendations-setting-card';
import type { RecommendationStrategy } from 'lumen/models';
import type { AllowedRecommendationTypeSettings } from 'lumen/models/recommendation-strategy';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';

type RecommendationStrategyOption = {
  id: AllowedRecommendationTypeSettings['allowed'];
  isChecked: boolean;
  label: string;
};

interface SettingsRecommendationsRecommendationTypeSignature {
  Args: {
    model: RecommendationsModel;
    strategy: RecommendationStrategy | null;
  };
}

export default class SettingsRecommendationsRecommendationType extends Component<SettingsRecommendationsRecommendationTypeSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get recommendationTypeOptions() {
    return [
      {
        id: 'all',
        label: this.intl.t(
          'pricing_assistant.recommendations.recommendations_type.label.all',
        ),
        isChecked: this.selectedRecommendationType === 'all',
      },
      {
        id: 'increase',
        label: this.intl.t(
          'pricing_assistant.recommendations.recommendations_type.label.only_increases',
        ),
        isChecked: this.selectedRecommendationType === 'increase',
      },
      {
        id: 'decrease',
        label: this.intl.t(
          'pricing_assistant.recommendations.recommendations_type.label.only_decreases',
        ),
        isChecked: this.selectedRecommendationType === 'decrease',
      },
    ] as RecommendationStrategyOption[];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRecommendationType() {
    return (this.strategy?.settings as AllowedRecommendationTypeSettings)
      .allowed;
  }

  @action
  async toggleOption(
    clickedOption: RecommendationStrategyOption,
  ): Promise<void> {
    if (
      this.strategy &&
      this.strategy.settings.settings_type === 'allowed_recommendation_type'
    ) {
      this.strategy.settings = {
        ...this.strategy.settings,
        allowed: clickedOption.id,
      };

      await this.args.model.saveStrategyTask.perform(this.strategy);

      if (clickedOption.id === 'all') {
        this.usageAnalytics.track(
          'Price increases & decreases settings selected',
          {
            eventTrigger: 'recommendation-settings',
            product: 'pricing-manager',
          },
        );
      } else if (clickedOption.id === 'increase') {
        this.usageAnalytics.track('Price only increases settings selected', {
          eventTrigger: 'recommendation-settings',
          product: 'pricing-manager',
        });
      } else if (clickedOption.id === 'decrease') {
        this.usageAnalytics.track('Price only decreases settings selected', {
          eventTrigger: 'recommendation-settings',
          product: 'pricing-manager',
        });
      }
    }
  }

  <template>
    <SettingsRecommendationsSnowflakesRecommendationsSettingCard
      @description={{t
        "pricing_assistant.recommendations.settings.price_increase_decrease.description"
      }}
      @illustration="40x40_trending-up-down"
      @title={{t
        "pricing_assistant.recommendations.settings.price_increase_decrease.title"
      }}
    >
      <:content>
        <div class="flex flex-row gap-200">
          {{#each this.recommendationTypeOptions as |option|}}
            <GeneralButtonsChoiceSegmentButton
              @isChecked={{option.isChecked}}
              @isDisabled={{this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled}}
              @label={{option.label}}
              @onClick={{fn this.toggleOption option}}
              @type="radio"
            />
          {{/each}}
        </div>
      </:content>
    </SettingsRecommendationsSnowflakesRecommendationsSettingCard>
  </template>
}
